import {
  AutoComplete,
  Button,
  Checkbox,
  RadioGroup,
  SliderModal
} from '@see-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import * as service from '@services/api/member-pharmacy-service';
import { useGlobalStore } from '@services/store/global-store';
import { Search, XIcon } from 'lucide-react';

interface Props {
  allergies: any[];
  onClose: () => void;
  onSubmit: () => void;
}

const UpdateAllergySlider: React.FC<Props> = ({
  allergies,
  onClose,
  onSubmit
}) => {
  const [memberAllergies, setMemberAllergies] = React.useState<any[]>([
    allergies
  ]);
  const [selectedAction, setSelectedAction] = useState<string>(
    allergies && allergies.length && allergies[0]?.allergies.length > 0
      ? 'Yes'
      : 'None'
  );
  const { memberDetails } = useGlobalStore((state) => state);
  const [defaultAllergies, setDefaultAllergies] = React.useState<any[]>([]);
  const [otherAllergiesList, setOtherAllergiesList] = React.useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [enableSearchResults, setEnableSearchResults] =
    useState<boolean>(false);
  const [selectedAllergies, setSelectedAllergies] = React.useState<number[]>(
    allergies[0].allergies.map((allergy) => allergy.id)
  );
  const [customAllergies, setCustomAllergies] = useState<any[]>([]);
  const fetchDefaultAllergies = async () => {
    try {
      const response = await service.getPharmacyDefaultAllergies({});
      setDefaultAllergies(response?.data?.allergies);
      if (
        selectedAllergies.length !== 0 &&
        response?.data?.allergies.length > 0
      ) {
        // Allergies in allergies[0].allergies that are NOT in defaultAllergies
        const custom = allergies[0].allergies.filter(
          (allergy: any) =>
            !response?.data?.allergies.some(
              (item: any) => item.id === allergy.id
            )
        );
        setCustomAllergies(custom);
      }
    } catch (error) {
      console.error('Error fetching default allergies:', error);
    }
  };

  const handleCheckboxChange = (allergyId: number) => {
    setSelectedAllergies((prevSelected) =>
      prevSelected.includes(allergyId)
        ? prevSelected.filter((id) => id !== allergyId)
        : [...prevSelected, allergyId]
    );
  };

  const onSubmitAllergy = async () => {
    let updatedAllergies: any = [];
    selectedAction === 'None'
      ? []
      : selectedAllergies.forEach((id) => {
          const allergy = defaultAllergies.find((allergy) => allergy.id === id);
          if (allergy) {
            updatedAllergies.push({ id: allergy.id, name: allergy.name });
          }
        });
    updatedAllergies = [
      ...updatedAllergies,
      ...customAllergies.map((allergy) => ({
        id: parseInt(allergy.id),
        name: allergy.name
      }))
    ];
    const response = await service.updatePharmacyAllergies({
      hcid: memberDetails?.member?.memberPolicy?.hcid,
      allergies: updatedAllergies,
      patientIdentifier: {
        naturalPatientId: {
          firstName: memberAllergies[0].firstName,
          lastName: memberAllergies[0].lastName,
          dob: memberAllergies[0].dob,
          hcid: memberDetails?.member?.memberPolicy?.hcid,
          prsn: '10'
        }
      }
    });
    onSubmit();
  };
  useEffect(() => {
    fetchDefaultAllergies();
    setMemberAllergies(allergies);
  }, []);

  const getOptions = () => {
    return otherAllergiesList.map((allergy) => {
      return {
        label: allergy.name,
        value: allergy.id.toString()
      };
    });
  };

  const handleChange = (option: { label: string; value: string }) => {
    const alreadyExists = customAllergies.some(
      (allergy) => allergy.id === option.value
    );
    if (!alreadyExists) {
      setCustomAllergies((prev) => [
        ...prev,
        { id: option.value, name: option.label }
      ]);
    }
    setEnableSearchResults(false);
  };

  return (
    <>
      <SliderModal
        direction="right"
        labelAriaCloseBtn="Close Modal"
        onClose={onClose}
        open
      >
        <SliderModal.Body className="p-12">
          <h2 className="mb-4">
            Allergies For {memberAllergies[0].firstName}{' '}
            {memberAllergies[0].lastName}
          </h2>
          <span className="block mb-4">
            We will need to know about any allergies before we can complete this
            order. If no allergies, please select None.
          </span>
          <RadioGroup
            className="g-4"
            layout="column"
            options={[
              {
                label: 'None',
                value: 'None'
              },
              {
                label: `${memberAllergies[0].firstName} ${memberAllergies[0].lastName} has the following allergies`,
                value: 'Yes'
              }
            ]}
            value={selectedAction}
            onValueChange={(value) => setSelectedAction(value)}
          />
          {selectedAction === 'Yes' && (
            <>
              {defaultAllergies.length > 0 && (
                <div className="mt-6 ml-6">
                  <div className="grid grid-cols-2 gap-4">
                    {defaultAllergies.map((allergy) => (
                      <label
                        key={allergy.id}
                        className="flex items-center space-x-3"
                      >
                        <Checkbox
                          checked={selectedAllergies.includes(allergy.id)}
                          // onChange={() => handleCheckboxChange(allergy.id)}
                          onCheckedChange={() =>
                            handleCheckboxChange(allergy.id)
                          }
                        />
                        <span>{allergy.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex w-1/3 mr-4 relative">
            <input
              type="text"
              placeholder={'Search other allergies (min 3 characters)...'}
              value={searchText}
              onChange={async (e) => {
                const value = e.target.value;
                setSearchText(value);
                if (value.length >= 3) {
                  setEnableSearchResults(true);
                  const res = await service.getAllergiesByName({
                    searchText: value
                  });
                  setOtherAllergiesList(res?.data?.allergies || []);
                } else {
                  setEnableSearchResults(false);
                }
              }}
              className="flex-grow pl-10"
            />
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500" />
            {searchText && (
              <button
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                onClick={() => {
                  setSearchText('');
                  setEnableSearchResults(false);
                  setOtherAllergiesList([]);
                }}
              >
                <XIcon />
              </button>
            )}

            {searchText && enableSearchResults && (
              <div className="absolute bg-white border border-black rounded shadow-md w-full z-10">
                <ul>
                  {getOptions()
                    .filter((option) =>
                      option.label
                        .toLowerCase()
                        .includes(searchText.toLowerCase())
                    )
                    .map((option, index) => (
                      <li
                        key={index}
                        className="p-2 hover:bg-blue-200 cursor-pointer flex justify-between items-center"
                        onClick={() => {
                          setSearchText(option.label);
                          handleChange(option);
                        }}
                      >
                        {option.label}

                        {searchText === option.label && (
                          <button
                            className="text-gray-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchText('');
                              setEnableSearchResults(false);
                              // setFilteredMemberData(memberData);
                            }}
                          >
                            <XIcon />
                          </button>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
          {customAllergies.length > 0 && (
            <div className="mt-2">
              <span className="font-semibold">Added Allergies:</span>
              <ul className="list-disc ml-6">
                {customAllergies.map((allergy) => (
                  <li
                    key={allergy.id}
                    className=" bg-blue-100 rounded px-2 py-1 mb-2"
                  >
                    <span className="text-blue-800">{allergy.name}</span>
                    <button
                      className="ml-2 text-gray-500 hover:text-red-600"
                      onClick={() =>
                        setCustomAllergies((prev) =>
                          prev.filter((a) => a.id !== allergy.id)
                        )
                      }
                      aria-label="Remove allergy"
                    >
                      <XIcon size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </SliderModal.Body>
        <SliderModal.Footer>
          <Button variant="primary" onClick={onSubmitAllergy}>
            Submit
          </Button>
          <Button variant="secondary" className="ml-4" onClick={onClose}>
            Close
          </Button>
        </SliderModal.Footer>
      </SliderModal>
    </>
  );
};

export default UpdateAllergySlider;
